import React, { useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import { API_BASE_URL } from './config';
import { formatLocalDateTime } from './timezone';

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--bg-modal-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
};

const modalContentStyle = {
    backgroundColor: 'var(--bg-modal)',
    borderRadius: '8px',
    width: '86%',
    maxWidth: '1120px',
    maxHeight: '88%',
    overflow: 'hidden',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-color)',
    boxShadow: '0 4px 6px var(--shadow-strong)',
    display: 'flex',
    flexDirection: 'column'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse'
};

const thStyle = {
    textAlign: 'left',
    padding: '10px',
    borderBottom: '1px solid var(--border-color)',
    color: 'var(--text-secondary)',
    fontSize: '12px'
};

const tdStyle = {
    padding: '10px',
    borderBottom: '1px solid var(--border-color)',
    fontSize: '12px'
};

const btnStyle = (bg, disabled = false) => ({
    padding: '8px 12px',
    background: disabled ? 'var(--button-disabled-bg)' : bg,
    color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
    border: 'none',
    borderRadius: '4px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '12px',
    fontWeight: 600,
    opacity: disabled ? 0.75 : 1
});

const parseDefinition = (definition) => {
    if (!definition) return { nodes: [], edges: [] };
    if (typeof definition === 'string') {
        try {
            return JSON.parse(definition);
        } catch (error) {
            return { nodes: [], edges: [] };
        }
    }
    if (typeof definition === 'object') {
        return definition;
    }
    return { nodes: [], edges: [] };
};

export default function WorkflowVersions({ workflowId, onClose, onRestored, canRestore }) {
    const [versions, setVersions] = useState([]);
    const [selectedVersionId, setSelectedVersionId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState(false);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const res = await axios.get(`${API_BASE_URL}/workflows/${workflowId}/versions?skip=0&limit=50`, { headers });
                const list = Array.isArray(res.data) ? res.data : [];
                setVersions(list);
                if (list.length > 0) {
                    setSelectedVersionId(list[0].id);
                }
            } catch (error) {
                console.error('Failed to fetch workflow versions', error);
                alert('Failed to fetch workflow versions');
            } finally {
                setLoading(false);
            }
        };

        if (workflowId) {
            fetchVersions();
        }
    }, [workflowId]);

    const selectedVersion = useMemo(
        () => versions.find((version) => version.id === selectedVersionId) || null,
        [versions, selectedVersionId]
    );

    const previewDefinition = useMemo(
        () => parseDefinition(selectedVersion?.definition),
        [selectedVersion]
    );

    const previewNodes = useMemo(
        () => (previewDefinition.nodes || []).map((node) => ({ ...node, type: 'icon' })),
        [previewDefinition]
    );

    const previewEdges = useMemo(
        () => previewDefinition.edges || [],
        [previewDefinition]
    );

    const handleRestore = async () => {
        if (!selectedVersion) return;
        if (!(await window.confirm(`Revert to version #${selectedVersion.version_number}? This will overwrite the current workflow.`))) {
            return;
        }
        try {
            setRestoring(true);
            const payload = {
                name: selectedVersion.name,
                definition: parseDefinition(selectedVersion.definition),
                save_history: selectedVersion.save_history,
                cron_schedule: selectedVersion.cron_schedule || '',
                is_paused: selectedVersion.is_paused,
                max_concurrent_runs: selectedVersion.max_concurrent_runs || 3
            };
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.put(`${API_BASE_URL}/workflows/${workflowId}`, payload, { headers });
            if (onRestored) {
                onRestored(res.data);
            }
            alert(`Workflow restored to version #${selectedVersion.version_number}`);
            onClose();
        } catch (error) {
            console.error('Failed to restore workflow version', error);
            alert('Failed to restore workflow version');
        } finally {
            setRestoring(false);
        }
    };

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border-color)' }}>
                    <h2 style={{ margin: 0, fontSize: '18px' }}>Workflow Versions</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '20px' }}>×</button>
                </div>

                {loading ? (
                    <div style={{ padding: '20px' }}>Loading versions...</div>
                ) : versions.length === 0 ? (
                    <div style={{ padding: '20px', color: 'var(--text-tertiary)' }}>No saved versions found for this workflow.</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '0', minHeight: '560px', height: '100%' }}>
                        <div style={{ borderRight: '1px solid var(--border-color)', overflowY: 'auto' }}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Version</th>
                                        <th style={thStyle}>Saved At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {versions.map((version) => (
                                        <tr
                                            key={version.id}
                                            onClick={() => setSelectedVersionId(version.id)}
                                            style={{
                                                cursor: 'pointer',
                                                background: selectedVersionId === version.id ? 'var(--bg-tertiary)' : 'transparent'
                                            }}
                                        >
                                            <td style={tdStyle}>#{version.version_number}</td>
                                            <td style={tdStyle}>{formatLocalDateTime(version.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            {selectedVersion && (
                                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ fontWeight: 700 }}>Preview Version #{selectedVersion.version_number}</div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                                            Name: {selectedVersion.name} · Nodes: {(previewDefinition.nodes || []).length} · Edges: {(previewDefinition.edges || []).length}
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleRestore}
                                        disabled={!canRestore || restoring}
                                        style={btnStyle('var(--warning-color)', !canRestore || restoring)}
                                        title={canRestore ? 'Revert workflow to selected version' : 'Viewers cannot revert workflows'}
                                    >
                                        {restoring ? 'Reverting...' : 'Revert to This Version'}
                                    </button>
                                </div>
                            )}

                            <div style={{ flex: 1, minHeight: '460px', background: 'var(--bg-node)' }}>
                                <ReactFlowProvider>
                                    <ReactFlow
                                        nodes={previewNodes}
                                        edges={previewEdges}
                                        fitView
                                        nodesDraggable={false}
                                        nodesConnectable={false}
                                        elementsSelectable={false}
                                        zoomOnScroll={false}
                                        panOnScroll
                                        style={{ background: 'var(--bg-node)' }}
                                    >
                                        <Background variant="dots" gap={12} size={1} color="var(--text-tertiary)" />
                                    </ReactFlow>
                                </ReactFlowProvider>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
